import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container } from "../../../globalstyled";
import { useAccess } from "../../../hooks/useAccess";
import AppLoader from "../../../components/loader";
import {
  useCustomerById,
  useFetchProductSytems,
  useFetchCustomerProjects
} from "../../../apolloClient/hooks/general/fetchHook";
import { TapButton, AppCSS, Spacer, TxtInput } from "../../../components";
import { Lbl } from "../../../components/labels";
import { ColDiv, RowDiv } from "../../../components/misc/misc.styled";
import { Stepper, Step, StepLabel, Box, useTheme, useMediaQuery } from "@mui/material";
import { toast } from "react-toastify";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { useCreateAddress, useCreateProject } from "../../../apolloClient/hooks/general/createHook";

interface CustomerProjectCreateProps {}

export enum PayType {
  INSURANCE = "INSURANCE",
  CASH = "CASH",
}

const steps = [
  "Address Selection",
  "Product System",
  "Payment Mode",
];

export const CustomerProjectCreate: React.FC<CustomerProjectCreateProps> = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const { loading: accessLoading } = useAccess();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const customerIdNum = parseInt(customerId || '0');

  const { customer, loading: customerLoading, error: customerError } = useCustomerById({
    id: customerIdNum,
  });
  const { productSystems, loading: productLoading, error: productError } = useFetchProductSytems();
  const { refetch: refetchCustomerProjects } = useFetchCustomerProjects({
    customerId: customerIdNum,
  });
  const { createAddress } = useCreateAddress();
  const { createProject } = useCreateProject();

  // Stepper state
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Step data states
  const [selectedAddress, setSelectedAddress] = useState<any>(null);
  const [selectedExistingAddressId, setSelectedExistingAddressId] = useState<number | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState<PayType | null>(null);

  // Set default selected address when customer data loads
  useEffect(() => {
    if (customer && customer.address && customer.address.length > 0 && selectedExistingAddressId === null) {
      setSelectedExistingAddressId(customer.address[0].id);
    }
  }, [customer, selectedExistingAddressId]);

  // Address handling states
  const [searchQuery, setSearchQuery] = useState("");
  const [predictions, setPredictions] = useState(false);

  const {
    placePredictions,
    getPlacePredictions,
  } = usePlacesService({
    apiKey: "AIzaSyBzepvXcfi4uJL7LpF-aeKW8FVUdDxTonA",
  });

  const onPlaceChanged = (pickedPlace: any) => {
    setSelectedAddress(pickedPlace);
    setSelectedExistingAddressId(null); // Clear existing address selection
    setSearchQuery(pickedPlace.description);
    setPredictions(false);
    console.log("New address selected:", pickedPlace);
    console.log("Existing address cleared");
  };

  const handleAddressInputChange = (value: string) => {
    setSearchQuery(value);
    setSelectedExistingAddressId(null); // Clear existing address selection when typing
    if (value.length > 0) {
      setPredictions(true);
      getPlacePredictions({ input: value });
    } else {
      setPredictions(false);
    }
  };


  const handleBack = () => {
    navigate(`/customers/${customerId}`);
  };

  const handleStepBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleStepNext = async () => {
    let isValid = false;

    switch (activeStep) {
      case 0:
        isValid = await handleAddressValidation();
        break;
      case 1:
        isValid = handleProductSelection();
        break;
      case 2:
        isValid = await handlePaymentModeSelection();
        break;
      default:
        isValid = true;
    }

    if (isValid && activeStep < 2) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleProductSelection = () => {
    if (selectedProducts.length === 0) {
      toast.error("Please select at least one product system.");
      return false;
    }
    return true;
  };

  const handlePaymentModeSelection = async () => {
    if (!selectedPaymentMode) {
      toast.error("Please select a payment mode.");
      return false;
    }

    // After payment mode selection, create the project directly
    await handleCreateProject();
    return true;
  };

  const toggleProductSelection = (id: number) => {
    setSelectedProducts((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((productId) => productId !== id)
        : [...prevSelected, id]
    );
  };

  // Address handling functions (based on reference code)
  const parseCityAndState = (address: string) => {
    const regex = /^([\w\s]+?),\s*([\w\s]+?),\s*([\w\s]+)$/;
    const matches = address.match(regex);
    if (matches) {
      const city = matches[1].trim();
      const state = matches[2].trim();
      return {
        city: city,
        state: state,
      };
    }
    return null;
  };


  const handleAddressValidation = async () => {
    console.log("Address validation - selectedAddress:", selectedAddress);
    console.log("Address validation - selectedExistingAddressId:", selectedExistingAddressId);

    // If a new address is selected, prioritize it
    if (selectedAddress) {
      const cityAndState = parseCityAndState(
        selectedAddress.structured_formatting?.secondary_text || ""
      );

      if (!cityAndState) {
        toast.error("Invalid address format. Please select a valid address.");
        return false;
      }

      // Prepare the address input
      const addressInput = {
        street: selectedAddress.structured_formatting?.main_text || "",
        city: cityAndState?.city || "",
        state: cityAndState?.state || "",
        placeId: selectedAddress.place_id || "",
        customerId: customerIdNum,
      };

      // Check if customer already has this address
      const existingAddress = customer.address?.find(
        (address: any) =>
          address.street === addressInput.street &&
          address.city === addressInput.city &&
          address.state === addressInput.state
      );

      if (existingAddress) {
        // Use existing address
        localStorage.setItem("addressId", existingAddress.id.toString());
        toast.success("Using existing address");
        return true;
      }

      try {
        const createAddressResponse = await createAddress(addressInput);
        if (createAddressResponse && createAddressResponse.id) {
          localStorage.setItem("addressId", createAddressResponse.id.toString());
          toast.success("Address created successfully");
          return true;
        }
      } catch (error) {
        console.error('Error creating address:', error);
        toast.error("Error creating address. Please try again!");
        return false;
      }

      return false;
    }

    // If no new address is selected but an existing address is selected, use it
    if (selectedExistingAddressId) {
      localStorage.setItem("addressId", selectedExistingAddressId.toString());
      toast.success("Using selected customer address");
      return true;
    }

    // If neither is selected
    toast.error("Please select an address or add a new one");
    return false;
  };

  const handleCreateProject = async () => {
    const addressId = Number(localStorage.getItem("addressId"));
    if (!addressId || selectedProducts.length === 0 || !selectedPaymentMode) {
      toast.error("Missing required data. Please complete all steps.");
      return;
    }

    setIsSubmitting(true);
    try {
      const projectInput = {
        productIds: selectedProducts,
        paymentType: selectedPaymentMode,
        addressId: addressId,
        userId: customerIdNum,
        isAdmin: true,
      };

      const createProjectResponse = await createProject(projectInput);
      if (createProjectResponse && createProjectResponse.id) {
        toast.success("Project created successfully!");
        // Refetch customer projects to update the list
        await refetchCustomerProjects();
        // Clear localStorage
        localStorage.removeItem("addressId");
        navigate(`/customers/${customerId}`);
        return;
      }
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error("Failed to create project. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };


  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderAddressStep();
      case 1:
        return renderProductSystemStep();
      case 2:
        return renderPaymentModeStep();
      default:
        return null;
    }
  };

  const renderAddressStep = () => (
    <ColDiv>
      <Lbl title="PROJECT ADDRESS" size={isSmallScreen ? 16 : 18} color={AppCSS.BLACK} bold={600} />
      <Spacer size={isSmallScreen ? 0.5 : 1} direction="col" />

      {/* New Address Input Section */}
      <ColDiv style={{ marginBottom: isSmallScreen ? "15px" : "20px" }}>
        <Lbl
          title="Add a new address:"
          size={isSmallScreen ? 14 : 16}
          color={AppCSS.BLACK}
          bold={500}
          backgroundColor="transparent"
        />
        <Spacer size={isSmallScreen ? 0.5 : 1} direction="col" />

        <div style={{
          position: "relative",
          width: "100%",
          maxWidth: isSmallScreen ? "100%" : "500px"
        }}>
          <TxtInput
            placeholder={isSmallScreen ? "Search address..." : "Search for project address..."}
            value={searchQuery}
            onChange={handleAddressInputChange}
            width={isSmallScreen ? 280 : 500}
            borderColor={AppCSS.GRAY_LIGHT}
          />
          {predictions && placePredictions.length > 0 && (
            <div style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 1000,
              backgroundColor: "white",
              border: "1px solid #ccc",
              borderRadius: "4px",
              maxHeight: "200px",
              overflowY: "auto",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
              maxWidth: "100%",
              width: "100%"
            }}>
              {placePredictions.map((item: any, index: number) => (
                <div
                  key={index}
                  style={{
                    padding: isSmallScreen ? "8px 12px" : "10px 15px",
                    borderBottom: index < placePredictions.length - 1 ? "1px solid #eee" : "none",
                    cursor: "pointer",
                    fontSize: isSmallScreen ? "13px" : "14px",
                    color: AppCSS.BLACK,
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                    lineHeight: isSmallScreen ? "1.3" : "1.4"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f0f0f0";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "white";
                  }}
                  onClick={() => onPlaceChanged(item)}
                >
                  {item.description}
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedAddress && (
          <ColDiv style={{
            marginTop: isSmallScreen ? "10px" : "15px",
            backgroundColor: "#e8f5e8",
            padding: isSmallScreen ? "8px" : "10px",
            borderRadius: "6px",
            border: "1px solid #c8e6c8"
          }}>
            <Lbl
              title="✓ Selected address:"
              size={isSmallScreen ? 11 : 12}
              color="#2d5a2d"
              bold={500}
              backgroundColor="transparent"
            />
            <Lbl
              title={selectedAddress.description || searchQuery}
              size={isSmallScreen ? 12 : 14}
              color={AppCSS.BLACK}
              bold={500}
              backgroundColor="transparent"
            />
          </ColDiv>
        )}
      </ColDiv>

      {/* Existing Addresses Section */}
      {customer.address && customer.address.length > 0 ? (
        <ColDiv>
          <Lbl title="Or select from existing addresses:" size={isSmallScreen ? 12 : 14} color={AppCSS.GRAY} backgroundColor="transparent"/>
          <Spacer size={isSmallScreen ? 0.5 : 1} direction="col" />

          {customer.address.map((address: any) => (
            <div
              key={address.id}
              onClick={() => {
                setSelectedExistingAddressId(address.id);
                setSelectedAddress(null); // Clear new address selection
                setSearchQuery(""); // Clear search
                setPredictions(false);
                console.log("Existing address selected:", address.id);
                console.log("New address cleared");
              }}
              style={{
                backgroundColor: selectedExistingAddressId === address.id ? "#e6f0ff" : "#f8f9fa",
                border: selectedExistingAddressId === address.id ? "2px solid #007BFF" : "1px solid #dee2e6",
                borderRadius: "8px",
                padding: isSmallScreen ? "12px" : "15px",
                marginBottom: isSmallScreen ? "8px" : "10px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: selectedExistingAddressId === address.id ? "0 2px 8px rgba(0, 123, 255, 0.15)" : "0 1px 3px rgba(0, 0, 0, 0.1)"
              }}
              onMouseEnter={(e) => {
                if (selectedExistingAddressId !== address.id) {
                  e.currentTarget.style.backgroundColor = "#f0f0f0";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedExistingAddressId !== address.id) {
                  e.currentTarget.style.backgroundColor = "#f8f9fa";
                }
              }}
            >
              <RowDiv style={{ alignItems: "flex-start", gap: isSmallScreen ? "8px" : "10px" }}>
                <div style={{
                  width: isSmallScreen ? "18px" : "20px",
                  height: isSmallScreen ? "18px" : "20px",
                  borderRadius: "50%",
                  backgroundColor: selectedExistingAddressId === address.id ? "#007BFF" : "#dee2e6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "2px"
                }}>
                  {selectedExistingAddressId === address.id && (
                    <span style={{ color: "white", fontSize: isSmallScreen ? "10px" : "12px" }}>✓</span>
                  )}
                </div>
                <ColDiv style={{
                  flex: 1,
                  minWidth: 0, // Important for text wrapping in flex containers
                  wordWrap: "break-word",
                  overflow: "hidden"
                }}>
                  <div style={{
                    fontSize: isSmallScreen ? "13px" : "15px",
                    fontWeight: selectedExistingAddressId === address.id ? 600 : 400,
                    color: AppCSS.BLACK,
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                    lineHeight: "1.4",
                    marginBottom: "4px"
                  }}>
                    {`${address.street}, ${address.city}, ${address.state}`}
                  </div>
                  {address.zipCode && (
                    <div style={{
                      fontSize: isSmallScreen ? "11px" : "12px",
                      color: AppCSS.GRAY,
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                      lineHeight: "1.3"
                    }}>
                      ZIP: {address.zipCode}
                    </div>
                  )}
                </ColDiv>
              </RowDiv>
            </div>
          ))}
        </ColDiv>
      ) : null}
    </ColDiv>
  );

  const renderProductSystemStep = () => (
    <ColDiv>
      <Lbl title="SELECT PRODUCT SYSTEMS" size={18} color={AppCSS.BLACK} bold={600} />
      <Spacer size={1} direction="col" />
      <Lbl
        title="Choose the product systems you want to include in your project."
        size={14}
        color={AppCSS.GRAY}
      />
      <Spacer size={2} direction="col" />

      <div style={{
        display: "flex",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: "20px",
        maxHeight: "400px",
        overflowY: "auto",
        padding: "10px",
        border: "1px solid #ddd",
        borderRadius: "8px",
        backgroundColor: "#f9f9f9",
      }}>
        {Array.isArray(productSystems) &&
          productSystems
            .filter((product) => !product.archived)
            .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
            .map((product) => (
              <div
                key={product.id}
                onClick={() => toggleProductSelection(product.id)}
                style={{
                  border: selectedProducts.includes(product.id)
                    ? "2px solid #007BFF"
                    : "1px solid #ccc",
                  borderRadius: "8px",
                  padding: "10px",
                  width: isSmallScreen ? "90%" : "200px",
                  minWidth: isSmallScreen ? "90%" : "200px",
                  maxWidth: isSmallScreen ? "90%" : "200px",
                  textAlign: "center",
                  backgroundColor: selectedProducts.includes(product.id)
                    ? "#E6F0FF"
                    : "#fff",
                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  margin: isSmallScreen ? "0 auto" : undefined,
                }}
              >
                <img
                  src={product.thumbnail}
                  alt={product.name}
                  style={{
                    width: "100%",
                    height: "120px",
                    objectFit: "cover",
                    borderRadius: "8px",
                    marginBottom: "10px",
                  }}
                />
                <h4 style={{
                  margin: "10px 0",
                  fontSize: "16px",
                  color: "#333",
                }}>
                  {product.name}
                </h4>
                <p style={{ fontSize: "14px", color: "#777" }}>
                  {product.duration}
                </p>
              </div>
            ))}
      </div>
    </ColDiv>
  );

  const renderPaymentModeStep = () => (
    <ColDiv>
      <Lbl title="SELECT PAYMENT MODE" size={18} color={AppCSS.BLACK} bold={600} />
      <Spacer size={1} direction="col" />
      <Lbl
        title="Tell us how you plan to pay. We help you facilitate your payment options at a later stage during your project."
        size={14}
        color={AppCSS.GRAY}
      />
      <Spacer size={2} direction="col" />

      <div style={{
        display: "flex",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: "20px",
        maxHeight: "500px",
        overflowY: "auto",
        padding: "10px",
        border: "1px solid #ddd",
        borderRadius: "8px",
        backgroundColor: "#f9f9f9",
      }}>
        {[
          {
            id: PayType.INSURANCE,
            label: "Insurance proceeds",
            icon: "🏠",
          },
          { id: PayType.CASH, label: "Cash payment", icon: "💵" },
        ].map((option) => (
          <div
            key={option.id}
            onClick={() => setSelectedPaymentMode(option.id)}
            style={{
              border:
                selectedPaymentMode === option.id
                  ? "2px solid #007BFF"
                  : "1px solid #ccc",
              borderRadius: "8px",
              padding: "10px",
              width: isSmallScreen ? "90%" : "200px",
              minWidth: isSmallScreen ? "90%" : "200px",
              maxWidth: isSmallScreen ? "90%" : "200px",
              textAlign: "center",
              backgroundColor:
                selectedPaymentMode === option.id ? "#E6F0FF" : "#fff",
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
              cursor: "pointer",
              transition: "all 0.3s ease",
              margin: isSmallScreen ? "0 auto" : undefined,
            }}
          >
            <div style={{ fontSize: "40px", marginBottom: "10px" }}>
              {option.icon}
            </div>
            <h4 style={{
              margin: "10px 0",
              fontSize: "16px",
              color: "#333",
            }}>
              {option.label}
            </h4>
          </div>
        ))}
      </div>
    </ColDiv>
  );


  if (accessLoading || customerLoading || productLoading) {
    return <AppLoader isLoading={accessLoading || customerLoading || productLoading} />;
  }

  if (customerError || !customer || productError) {
    return (
      <Container>
        <ColDiv style={{ padding: "20px", alignItems: "center" }}>
          <Lbl
            title={`Error loading data: ${customerError?.message || productError?.message || 'Data not found'}`}
            color={AppCSS.RED}
            size={16}
          />
          <Spacer size={2} direction="col" />
          <TapButton
            title="Back to Customer"
            onTap={handleBack}
            bgColor={AppCSS.BLUE}
            color={AppCSS.WHITE}
            width={180}
            height={40}
          />
        </ColDiv>
      </Container>
    );
  }

  return (
    <Container>
      <ColDiv>
        <Spacer size={isSmallScreen ? 1 : 2} direction="col" />

        {/* Main Content Layout */}
        <div style={{
          width: "100%",
          maxWidth: "1200px",
          marginLeft: "auto",
          marginRight: "auto",
          paddingLeft: isSmallScreen ? "15px" : "20px",
          paddingRight: isSmallScreen ? "15px" : "20px",
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          gap: isSmallScreen ? "20px" : "30px",
          alignItems: "flex-start"
        }}>
          {/* Left Side - Customer Information */}
          <ColDiv style={{
            width: isSmallScreen ? "100%" : "300px",
            minWidth: "280px"
          }}>
            {/* Header Section - Above Customer Details */}
            <RowDiv style={{
              alignItems: "center",
              marginBottom: isSmallScreen ? "15px" : "20px",
              gap: "15px",
              flexWrap: "wrap"
            }}>
              <div
                onClick={handleBack}
                style={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  backgroundColor: AppCSS.BLUE,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#0056b3";
                  e.currentTarget.style.transform = "scale(1.05)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = AppCSS.BLUE;
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                <span style={{
                  color: "white",
                  fontSize: "18px",
                  fontWeight: "bold"
                }}>
                  ←
                </span>
              </div>
              <Lbl
                title="Create New Project"
                size={isSmallScreen ? 20 : 25}
                bold={600}
                backgroundColor="transparent"
              />
            </RowDiv>

            <ColDiv style={{
              backgroundColor: "#f8f9fa",
              borderRadius: "12px",
              padding: isSmallScreen ? "15px" : "20px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              position: isSmallScreen ? "relative" : "sticky",
              top: isSmallScreen ? "auto" : "20px",
              marginBottom: isSmallScreen ? "0" : "auto"
            }}>
              <Lbl
                title="CUSTOMER DETAILS"
                size={16}
                color={AppCSS.BLACK}
                bold={600}
                backgroundColor="transparent"
              />
              <Spacer size={2} direction="col" />

              <ColDiv style={{ gap: isSmallScreen ? "12px" : "15px" }}>
                <ColDiv>
                  <Lbl title="Name" size={12} color={AppCSS.GRAY} bold={500} backgroundColor="transparent"/>
                  <Lbl
                    title={customer.firstName && customer.lastName
                      ? `${customer.firstName} ${customer.lastName}`
                      : customer.firstName || customer.lastName || 'N/A'}
                    size={15}
                    color={AppCSS.BLACK}
                    bold={500}
                    backgroundColor="transparent"
                  />
                </ColDiv>

                <ColDiv>
                  <Lbl title="Email" size={12} color={AppCSS.GRAY} bold={500} backgroundColor="transparent"/>
                  <Lbl
                    title={customer.email}
                    size={14}
                    color={AppCSS.BLACK}
                    backgroundColor="transparent"
                  />
                </ColDiv>

                <ColDiv>
                  <Lbl title="Phone" size={12} color={AppCSS.GRAY} bold={500} backgroundColor="transparent"/>
                  <Lbl
                    title={customer.phone || 'N/A'}
                    size={14}
                    color={AppCSS.BLACK}
                    backgroundColor="transparent"
                  />
                </ColDiv>

                {customer.address && customer.address.length > 0 && (
                  <ColDiv>
                    <Lbl title="Address" size={12} color={AppCSS.GRAY} bold={500} backgroundColor="transparent"/>
                    <Lbl
                      title={`${customer.address[0].street}, ${customer.address[0].city}, ${customer.address[0].state}`}
                      size={14}
                      color={AppCSS.BLACK}
                      backgroundColor="transparent"
                    />
                  </ColDiv>
                )}
              </ColDiv>
            </ColDiv>
          </ColDiv>

          {/* Right Side - Project Creation Steps */}
          <ColDiv style={{
            flex: 1,
            minWidth: isSmallScreen ? "100%" : "600px"
          }}>
            {/* Stepper - Moved up to align with customer details */}
            <Box sx={{
              width: "100%",
              marginBottom: isSmallScreen ? "15px" : "20px",
              marginTop: isSmallScreen ? "0" : "10px"
            }}>
              <Stepper
                activeStep={activeStep}
                alternativeLabel={!isSmallScreen}
                orientation={isSmallScreen ? "vertical" : "horizontal"}
              >
                {steps.map((label, index) => (
                  <Step key={index}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            {/* Step Content */}
            <ColDiv style={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              padding: isSmallScreen ? "20px" : "30px",
              minHeight: isSmallScreen ? "400px" : "500px",
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
              border: "1px solid #e9ecef"
            }}>
              {renderStepContent(activeStep)}
            </ColDiv>

            <Spacer size={1.5} direction="col" />

            {/* Navigation Buttons */}
            <div style={{
              display: "flex",
              flexDirection: isSmallScreen ? "column" : "row",
              justifyContent: isSmallScreen ? "center" : "space-between",
              gap: isSmallScreen ? "15px" : "0",
              alignItems: "center"
            }}>
              <TapButton
                title="Back"
                onTap={activeStep === 0 ? handleBack : handleStepBack}
                bgColor={AppCSS.GRAY_LIGHT}
                color={AppCSS.BLACK}
                width={isSmallScreen ? 250 : 120}
                height={45}
                radius={8}
              />
              {activeStep === 2 ? (
                <TapButton
                  title={isSubmitting ? "Creating Project..." : "Select Payment & Create"}
                  onTap={handleStepNext}
                  bgColor={AppCSS.BLUE}
                  color={AppCSS.WHITE}
                  width={isSmallScreen ? 250 : 220}
                  height={45}
                  radius={8}
                  disable={isSubmitting}
                />
              ) : (
                <TapButton
                  title="Next"
                  onTap={handleStepNext}
                  bgColor={AppCSS.BLUE}
                  color={AppCSS.WHITE}
                  width={isSmallScreen ? 250 : 120}
                  height={45}
                  radius={8}
                />
              )}
            </div>
          </ColDiv>
        </div>

        <Spacer size={3} direction="col" />
      </ColDiv>
    </Container>
  );
};